import { ChevronDown } from "lucide-react";
import type { RefObject } from "react";
import type { MessageRole } from "../../lib/types";
import { ChatBubble } from "./ChatBubble";
import { TypingIndicator } from "./TypingIndicator";

interface Message {
  role: MessageRole;
  content: string;
}

interface ChatMessagesProps {
  messages: Message[];
  pendingMessage: string | null;
  isReadOnly: boolean;
  isTyping: boolean;
  showIntroModal: boolean;
  isNearBottom: boolean;
  isTtsEnabled: boolean;
  audioStates: Map<number, { status: "idle" | "loading" | "playing" | "ready" }>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  typewriterEndAtRef: RefObject<number | null>;
  onScroll: () => void;
  onScrollToBottom: () => void;
  onVocalize: (idx: number, text: string) => void;
}

export function ChatMessages({
  messages,
  pendingMessage,
  isReadOnly,
  isTyping,
  showIntroModal,
  isNearBottom,
  isTtsEnabled,
  audioStates,
  scrollContainerRef,
  messagesEndRef,
  typewriterEndAtRef,
  onScroll,
  onScrollToBottom,
  onVocalize,
}: ChatMessagesProps) {
  return (
    <div className="relative flex-1 min-h-0">
      <div
        ref={scrollContainerRef}
        onScroll={onScroll}
        className="absolute inset-0 overflow-y-auto px-3 sm:px-6 space-y-4"
      >
        {!showIntroModal &&
          messages.map((msg, idx) => (
            <ChatBubble
              key={idx}
              role={msg.role}
              content={msg.content}
              isLatest={idx === messages.length - 1 && !pendingMessage}
              shouldTypeWrite={
                !isReadOnly &&
                idx === messages.length - 1 &&
                msg.role === "assistant" &&
                !isTyping &&
                !pendingMessage
              }
              onTypewriterComplete={
                idx === messages.length - 1 && msg.role === "assistant"
                  ? () => {
                      if (typewriterEndAtRef.current === null) {
                        typewriterEndAtRef.current = Date.now();
                      }
                    }
                  : undefined
              }
              onVocalize={
                isTtsEnabled && msg.role === "assistant"
                  ? () => onVocalize(idx, msg.content)
                  : undefined
              }
              audioStatus={
                isTtsEnabled
                  ? (audioStates.get(idx)?.status ?? "idle")
                  : undefined
              }
            />
          ))}
        {!showIntroModal && pendingMessage && (
          <ChatBubble
            role="user"
            content={pendingMessage}
            isLatest={true}
            shouldTypeWrite={false}
          />
        )}
        {!showIntroModal && isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {!isNearBottom && (
        <button
          onClick={onScrollToBottom}
          className="absolute bottom-4 right-6 flex items-center gap-2 px-4 py-3 rounded-full bg-card text-foreground shadow-xl border border-charte-line-strong animate-in fade-in zoom-in duration-200 transition-all hover:-translate-y-0.5 hover:border-charte-line-accent hover:shadow-2xl z-20 font-medium"
          title="Descendre vers les nouveaux messages"
        >
          <ChevronDown className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
